/**
 * Prioridad real de un ticket, en el lado del cliente.
 *
 * No es un campo guardado: se calcula al vuelo porque depende de HOY. Un
 * ticket que hoy es "normal" mañana es "crítico" sin que nadie lo toque, así
 * que persistirlo obligaría a recalcularlo por lo bajo cada día.
 *
 * Entradas: la urgencia que marca el staff (`prioridad`), la fecha comprometida
 * de entrega (`fecha_limite`), y el estado de atención (chat sin responder,
 * inactividad, sin técnico asignado). El resultado son cuatro niveles.
 *
 * Es el gemelo de `server/utils/prioridad.js`: si cambian los pesos aquí,
 * cambian allí.
 */
import { desdeIso, esFinDeSemana, hoy } from './fechaLimite';

/** De más a menos grave. El orden es el que usa la tabla al ordenar. */
export const NIVELES = ['vencido', 'critico', 'alto', 'normal'];

export const PESO_NIVEL = { vencido: 0, critico: 1, alto: 2, normal: 3 };

// Un ticket cerrado o retenido no compite por atención: el primero está hecho y
// el segundo está parado a propósito.
const ESTADOS_INACTIVOS = ['cerrado', 'retenido'];

const UMBRAL_ALERTA_HORAS = 4;
const UMBRAL_SIN_ACTIVIDAD_HORAS = 120; // 5 días

/** Días hábiles que faltan hasta `destino` (negativo si ya pasó). */
export const diasHabilesHasta = (destino, desde = hoy()) => {
  if (!destino) return null;
  const ida = destino >= desde;
  const inicio = ida ? new Date(desde) : new Date(destino);
  const fin = ida ? new Date(destino) : new Date(desde);
  let dias = 0;
  const cursor = new Date(inicio);
  while (cursor < fin) {
    cursor.setDate(cursor.getDate() + 1);
    if (!esFinDeSemana(cursor)) dias += 1;
  }
  return ida ? dias : -dias;
};

/**
 * Puntúa un ticket y lo coloca en un nivel.
 * @returns {{ nivel: string, puntos: number, diasHabiles: number|null, motivos: string[] }}
 *          `motivos` son claves de i18n (`prioridadTicket.motivos.*`), para que
 *          la interfaz explique por qué está donde está.
 */
export const calcularPrioridad = (ticket, ahora = Date.now()) => {
  const motivos = [];
  const limite = desdeIso(ticket?.fecha_limite);
  const diasHabiles = limite ? diasHabilesHasta(limite) : null;

  if (ESTADOS_INACTIVOS.includes(ticket?.estado_del_ticket)) {
    return { nivel: 'normal', puntos: 0, diasHabiles, motivos };
  }

  // La entrega vencida manda sobre todo lo demás
  if (diasHabiles !== null && diasHabiles < 0) {
    return { nivel: 'vencido', puntos: 100, diasHabiles, motivos: ['vencido'] };
  }

  let puntos = 0;

  if (ticket?.prioridad === 'urgente') {
    puntos += 40;
    motivos.push('urgente');
  }

  // Cuanto menos margen queda hasta la entrega, más pesa
  if (diasHabiles !== null) {
    if (diasHabiles <= 2) { puntos += 40; motivos.push('entregaInminente'); }
    else if (diasHabiles <= 5) { puntos += 25; motivos.push('entregaProxima'); }
    else if (diasHabiles <= 10) { puntos += 10; }
  }

  const horasInactivo = (ahora - new Date(ticket?.updatedAt || ticket?.createdAt).getTime()) / 3600000;

  if (ticket?.estado_del_chat === 'sin_responder' && horasInactivo >= UMBRAL_ALERTA_HORAS) {
    puntos += 15;
    motivos.push('esperandoRespuesta');
  }

  if (horasInactivo >= UMBRAL_SIN_ACTIVIDAD_HORAS) {
    puntos += 10;
    motivos.push('sinActividad');
  }

  if (ticket?.usuario_encargado === 'sin_asignar') {
    puntos += 10;
    motivos.push('sinAsignar');
  }

  // 25 = una entrega a menos de una semana ya sube de nivel por sí sola;
  // 65 = urgencia declarada y encima poco margen, o equivalente.
  const nivel = puntos >= 65 ? 'critico' : puntos >= 25 ? 'alto' : 'normal';
  return { nivel, puntos, diasHabiles, motivos };
};

/** Solo el nivel, para filtrar y contar sin arrastrar el resto. */
export const nivelPrioridad = (ticket, ahora) => calcularPrioridad(ticket, ahora).nivel;
